import { useCallback, useEffect, useState } from 'react';
import { InlineFieldError } from '@/components/ui/FormFeedback';
import { fetchAdminTeamInfo } from '@/api';
import type { Order } from '@/types/order';
import {
  ORDER_DURATION_MAX,
  ORDER_DURATION_MIN,
  ORDER_DURATION_STEP,
  clampOrderDuration,
  formatDurationHoursLabel,
  invoiceTaxRateToDecimal,
  resolveOrderDurationHours,
  type OrderBillingValues,
} from '@/lib/orderBilling';
import {
  buildAdminCreatePrices,
  exGstToIncGst,
  formatGstPercentInput,
  formatMoneyInput,
  incGstToExGst,
  parseGstPercentInput,
  parseMoneyInput,
} from '@/lib/orderPricing';

export interface OrderBillingErrors {
  duration?: string;
  gst?: string;
  priceExGst?: string;
  priceIncGst?: string;
}

interface OrderBillingFieldsProps {
  order: Order | null;
  submitting?: boolean;
  hideDuration?: boolean;
  hidePrice?: boolean;
  duration?: number;
  onDurationChange?: (next: number) => void;
  gstPercent: string;
  priceExGst: string;
  priceIncGst: string;
  errors: OrderBillingErrors;
  taxName?: string;
  taxError?: string;
  onGstPercentChange: (next: string) => void;
  onPriceExGstChange: (next: string) => void;
  onPriceIncGstChange: (next: string) => void;
}

export function OrderBillingFields({
  order,
  submitting = false,
  hideDuration = false,
  hidePrice = false,
  duration,
  onDurationChange,
  gstPercent,
  priceExGst,
  priceIncGst,
  errors,
  taxName = 'GST',
  taxError = '',
  onGstPercentChange,
  onPriceExGstChange,
  onPriceIncGstChange,
}: OrderBillingFieldsProps) {
  const hours = duration ?? resolveOrderDurationHours(order);

  return (
    <div className="space-y-3">
      {hideDuration ? null : (
        <div>
          <label className="block text-xs font-normal text-gray-600 mb-2">
            Work Duration <span className="text-red-500">*</span>
          </label>
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-600">Total hours</span>
              <span className="text-green-600 text-sm font-bold">
                {formatDurationHoursLabel(hours)} h
              </span>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={ORDER_DURATION_MIN}
                max={ORDER_DURATION_MAX}
                step={ORDER_DURATION_STEP}
                value={hours}
                disabled={submitting}
                onChange={(e) => onDurationChange?.(clampOrderDuration(Number(e.target.value)))}
                className="flex-1 h-2 accent-green-600 cursor-pointer disabled:opacity-60"
                aria-label="Work duration in hours"
              />
              <input
                type="number"
                min={ORDER_DURATION_MIN}
                max={ORDER_DURATION_MAX}
                step={ORDER_DURATION_STEP}
                value={hours}
                disabled={submitting}
                onChange={(e) => {
                  const parsed = Number(e.target.value);
                  if (!Number.isFinite(parsed)) return;
                  onDurationChange?.(clampOrderDuration(parsed));
                }}
                className="w-16 h-9 rounded-lg border border-gray-200 text-center text-sm font-semibold text-gray-800 bg-white disabled:opacity-60"
                aria-label="Work duration hours"
              />
            </div>
            <div className="flex items-center justify-between mt-2 text-[10px] text-gray-400">
              <span>{ORDER_DURATION_MIN}h</span>
              <span>{ORDER_DURATION_MAX}h</span>
            </div>
          </div>
          <InlineFieldError id="order-billing-duration-error" message={errors.duration} />
        </div>
      )}

      {hidePrice ? null : (
      <div>
        <div className="flex items-center justify-between gap-2 mb-2">
          <span className="text-xs font-semibold text-gray-600">
            Order Total <span className="text-red-500">*</span>
          </span>
          <label className="flex items-center gap-1.5 text-[11px] font-medium text-gray-500">
            {taxName} %
            <input
              inputMode="decimal"
              value={gstPercent}
              readOnly
              disabled={submitting || !gstPercent}
              onChange={(e) => onGstPercentChange(e.target.value)}
              className="w-14 h-7 rounded-md border border-gray-200 bg-gray-50 text-center text-xs font-semibold text-gray-800 outline-none disabled:opacity-60"
              aria-label={`${taxName} rate percent`}
              aria-invalid={!!errors.gst || !!taxError}
              aria-describedby={errors.gst || taxError ? 'order-billing-gst-error' : undefined}
            />
          </label>
        </div>
        <InlineFieldError id="order-billing-gst-error" message={taxError || errors.gst} />

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[11px] font-normal text-gray-500 mb-1.5">
              Ex-{taxName} (A$) <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
              <input
                inputMode="decimal"
                value={priceExGst}
                disabled={submitting || !gstPercent}
                onChange={(e) => onPriceExGstChange(e.target.value)}
                className="w-full h-9 pl-7 pr-3 rounded-lg border border-gray-200 text-sm text-gray-800 outline-none focus:border-green-500 disabled:opacity-60"
                placeholder="0.00"
                aria-invalid={!!errors.priceExGst}
                aria-describedby={errors.priceExGst ? 'order-billing-price-ex-error' : undefined}
              />
            </div>
            <InlineFieldError id="order-billing-price-ex-error" message={errors.priceExGst} />
          </div>
          <div>
            <label className="block text-[11px] font-normal text-gray-500 mb-1.5">
              Inc-{taxName} (A$) <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
              <input
                inputMode="decimal"
                value={priceIncGst}
                disabled={submitting || !gstPercent}
                onChange={(e) => onPriceIncGstChange(e.target.value)}
                className="w-full h-9 pl-7 pr-3 rounded-lg border border-gray-200 text-sm text-gray-800 outline-none focus:border-green-500 disabled:opacity-60"
                placeholder="0.00"
                aria-invalid={!!errors.priceIncGst}
                aria-describedby={errors.priceIncGst ? 'order-billing-price-inc-error' : undefined}
              />
            </div>
            <InlineFieldError id="order-billing-price-inc-error" message={errors.priceIncGst} />
          </div>
        </div>
        <p className="text-[10px] text-gray-400 leading-relaxed mt-2">
          {taxName} comes from invoice settings. Enter either amount and the other will be calculated.
        </p>
      </div>
      )}
    </div>
  );
}

export function useOrderBilling(order: Order | null) {
  const [duration, setDuration] = useState(resolveOrderDurationHours(order));
  const [gstPercent, setGstPercent] = useState('10');
  const [priceExGst, setPriceExGst] = useState('');
  const [priceIncGst, setPriceIncGst] = useState('');
  const [errors, setErrors] = useState<OrderBillingErrors>({});
  const [invoiceGst, setInvoiceGst] = useState<number | null>(null);
  const [taxName, setTaxName] = useState('GST');
  const [taxError, setTaxError] = useState('');
  const [taxReady, setTaxReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchAdminTeamInfo()
      .then((info) => {
        if (cancelled) return;
        const name = info.invoice_info?.tax_name?.trim();
        const rate = invoiceTaxRateToDecimal(info.invoice_info?.tax_rate);
        setTaxName(name || 'Tax');
        setInvoiceGst(rate);
        setTaxError(rate === null ? 'Invoice tax rate is not set.' : '');
      })
      .catch(() => {
        if (cancelled) return;
        setInvoiceGst(null);
        setTaxError('Failed to load invoice tax settings.');
      })
      .finally(() => {
        if (!cancelled) setTaxReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!taxReady) return;
    if (!order) {
      setDuration(resolveOrderDurationHours(null));
      setGstPercent(invoiceGst === null ? '' : formatGstPercentInput(invoiceGst));
      setPriceExGst('');
      setPriceIncGst('');
      setErrors({});
      return;
    }

    const gst = invoiceGst;
    setDuration(resolveOrderDurationHours(order));
    setGstPercent(gst === null ? '' : formatGstPercentInput(gst));

    let ex =
      typeof order.totalPrice === 'number' && order.totalPrice > 0 ? order.totalPrice : null;
    let inc =
      typeof order.totalPriceWithGst === 'number' && order.totalPriceWithGst > 0
        ? order.totalPriceWithGst
        : null;
    if (gst !== null && ex !== null) {
      inc = exGstToIncGst(ex, gst);
    } else if (gst !== null && inc !== null) {
      ex = incGstToExGst(inc, gst);
    }
    setPriceExGst(ex !== null ? formatMoneyInput(ex) : '');
    setPriceIncGst(inc !== null ? formatMoneyInput(inc) : '');
    setErrors({});
  }, [order, invoiceGst, taxReady]);

  const handleGstPercentChange = useCallback((next: string) => {
    setGstPercent(next);
    setErrors((prev) => ({ ...prev, gst: undefined, priceIncGst: undefined }));
    const gst = parseGstPercentInput(next);
    const ex = parseMoneyInput(priceExGst);
    if (gst !== null && ex !== null && ex > 0) {
      setPriceIncGst(formatMoneyInput(exGstToIncGst(ex, gst)));
    }
  }, [priceExGst]);

  const handlePriceExGstChange = useCallback((next: string) => {
    setPriceExGst(next);
    setErrors((prev) => ({ ...prev, priceExGst: undefined, priceIncGst: undefined }));
    const gst = parseGstPercentInput(gstPercent);
    const parsed = parseMoneyInput(next);
    if (parsed !== null && gst !== null) {
      setPriceIncGst(formatMoneyInput(exGstToIncGst(parsed, gst)));
    }
  }, [gstPercent]);

  const handlePriceIncGstChange = useCallback((next: string) => {
    setPriceIncGst(next);
    setErrors((prev) => ({ ...prev, priceExGst: undefined, priceIncGst: undefined }));
    const gst = parseGstPercentInput(gstPercent);
    const parsed = parseMoneyInput(next);
    if (parsed !== null && gst !== null) {
      setPriceExGst(formatMoneyInput(incGstToExGst(parsed, gst)));
    }
  }, [gstPercent]);

  const validate = useCallback((overrideDuration?: number): OrderBillingValues | null => {
    const nextDuration = clampOrderDuration(overrideDuration ?? duration);
    const gst = parseGstPercentInput(gstPercent);
    const parsedEx = parseMoneyInput(priceExGst);
    const parsedInc = parseMoneyInput(priceIncGst);
    const nextErrors: OrderBillingErrors = {};

    if (nextDuration < ORDER_DURATION_MIN) {
      nextErrors.duration = 'Work duration is required.';
    }
    if (gst === null) {
      nextErrors.gst = taxError || 'Invoice tax rate is not set.';
    }
    if (!parsedEx || parsedEx <= 0) {
      nextErrors.priceExGst = `Ex-${taxName} total is required.`;
    }
    if (!parsedInc || parsedInc <= 0) {
      nextErrors.priceIncGst = `Inc-${taxName} total is required.`;
    }

    if (gst !== null && parsedEx && parsedEx > 0 && parsedInc && parsedInc > 0) {
      const aligned = buildAdminCreatePrices(parsedEx, gst);
      if (Math.abs(aligned.totalPriceWithGst - parsedInc) > 0.02) {
        nextErrors.priceIncGst = `Inc-${taxName} total does not match the ${taxName} rate.`;
      }
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0 || gst === null || !parsedEx || !parsedInc) {
      return null;
    }

    const aligned = buildAdminCreatePrices(parsedEx, gst);
    return {
      serviceDuration: nextDuration,
      gstRateDecimal: gst,
      totalPrice: aligned.totalPrice,
      totalPriceWithGst: aligned.totalPriceWithGst,
    };
  }, [duration, gstPercent, priceExGst, priceIncGst, taxError, taxName]);

  const validateHours = useCallback((): number | null => {
    const nextDuration = clampOrderDuration(duration);
    if (nextDuration < ORDER_DURATION_MIN) {
      setErrors({ duration: 'Work duration is required.' });
      return null;
    }
    setErrors({});
    return nextDuration;
  }, [duration]);

  return {
    duration,
    setDuration,
    gstPercent,
    priceExGst,
    priceIncGst,
    errors,
    taxName,
    taxError,
    handleGstPercentChange,
    handlePriceExGstChange,
    handlePriceIncGstChange,
    validate,
    validateHours,
  };
}
